<template>
  <div class="dir-panel">
    <div class="dir-cab">
      <h4>Directivos</h4>
      <span class="dir-conteo">
        {{ vigentes.length }} vigente{{ vigentes.length === 1 ? '' : 's' }}
        <template v-if="salientes.length"> · {{ salientes.length }} anterior(es)</template>
      </span>
      <button class="btn-dir-nuevo" @click="abrirNuevo">+ Directivo</button>
    </div>

    <div v-if="cargando" class="dir-info">Cargando directivos…</div>
    <p v-else-if="!directivos.length" class="dir-vacio">
      Esta dependencia no tiene directivos registrados.
    </p>

    <table v-else class="dir-tabla">
      <thead>
        <tr><th>Cargo</th><th>Nombre</th><th>Acto</th><th>Desde</th><th>Hasta</th><th></th></tr>
      </thead>
      <tbody>
        <tr v-for="d in directivos" :key="d.id_directivo" :class="{ saliente: !d.vigente }">
          <td>
            {{ d.cargo }}
            <span v-if="!d.vigente" class="dir-badge">anterior</span>
          </td>
          <td>{{ d.nombre_completo }}</td>
          <td>{{ d.acto_nombramiento ?? '—' }}</td>
          <td>{{ d.fecha_desde ?? '—' }}</td>
          <td>{{ d.fecha_hasta ?? 'en el cargo' }}</td>
          <td class="dir-acciones">
            <button class="btn-dir" @click="abrirEditar(d)">Editar</button>
            <button class="btn-dir btn-dir-peligro" @click="eliminar(d)">Eliminar</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Alta / edición -->
    <div v-if="editando" class="dir-form">
      <h5>{{ editando.id_directivo ? 'Editar directivo' : 'Nuevo directivo' }}</h5>
      <div class="dir-grid">
        <label>Cargo *<input v-model="editando.cargo" class="dir-input" placeholder="Rector" /></label>
        <label>Nombre *<input v-model="editando.nombre" class="dir-input" /></label>
        <label>Apellido<input v-model="editando.apellido" class="dir-input" /></label>
        <label>Acto de nombramiento
          <input v-model="editando.acto_nombramiento" class="dir-input" placeholder="Acta 01, Sala General" />
        </label>
        <label>Fecha de la norma<input v-model="editando.fecha_norma" type="date" class="dir-input" /></label>
        <label>Desde<input v-model="editando.fecha_desde" type="date" class="dir-input" /></label>
        <label>
          Hasta
          <input v-model="editando.fecha_hasta" type="date" class="dir-input" />
          <small class="dir-ayuda">Vacío = sigue en el cargo</small>
        </label>
        <label>Correo<input v-model="editando.email" type="email" class="dir-input" /></label>
      </div>
      <div class="dir-form-acciones">
        <button class="btn-dir" @click="editando = null">Cancelar</button>
        <button
          class="btn-dir btn-dir-ok"
          :disabled="!editando.cargo || !editando.nombre || guardando"
          @click="guardar"
        >{{ guardando ? 'Guardando…' : 'Guardar' }}</button>
      </div>
      <p v-if="error" class="dir-error">{{ error }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Directivos de una dependencia.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, computed, watch, onMounted } from 'vue'
import {
  getDirectivosService,
  agregarDirectivoService,
  actualizarDirectivoService,
  eliminarDirectivoService,
  type Directivo
} from '@/services/InstitucionSniesService'

const props = defineProps<{ idArea: number }>()

const directivos = ref<Directivo[]>([])
const cargando = ref(false)
const guardando = ref(false)
const editando = ref<Partial<Directivo> | null>(null)
const error = ref('')

// Los que ya salieron se conservan: quién firmaba en cada momento es parte de
// la trazabilidad de los actos administrativos.
const vigentes = computed(() => directivos.value.filter(d => d.vigente))
const salientes = computed(() => directivos.value.filter(d => !d.vigente))

async function cargar() {
  cargando.value = true
  try {
    directivos.value = await getDirectivosService(props.idArea)
  } finally {
    cargando.value = false
  }
}

function abrirNuevo() {
  error.value = ''
  editando.value = { cargo: '', nombre: '', apellido: '', acto_nombramiento: '' }
}

function abrirEditar(d: Directivo) {
  error.value = ''
  editando.value = { ...d }
}

async function guardar() {
  if (!editando.value?.cargo || !editando.value?.nombre) return
  guardando.value = true
  error.value = ''
  try {
    const resultado = editando.value.id_directivo
      ? await actualizarDirectivoService(editando.value.id_directivo, editando.value)
      : await agregarDirectivoService(props.idArea, editando.value)

    if (!resultado.ok) {
      error.value = resultado.error ?? 'No se pudo guardar'
      return
    }
    editando.value = null
    await cargar()
  } finally {
    guardando.value = false
  }
}

async function eliminar(d: Directivo) {
  if (await eliminarDirectivoService(d.id_directivo)) await cargar()
}

watch(() => props.idArea, () => { editando.value = null; cargar() })
onMounted(cargar)
</script>

<style scoped>
.dir-panel { border-top: 1px dashed #d6dee6; margin-top: 0.9rem; padding-top: 0.8rem; }
.dir-cab { display: flex; align-items: center; gap: 0.6rem; }
.dir-cab h4 { margin: 0; font-size: 0.86rem; color: #14507d; }
.dir-conteo { font-size: 0.75rem; color: #9aa5b1; }
.btn-dir-nuevo {
  margin-left: auto; border: 1px solid #c6ddf7; background: #eef5fb; color: #0e69a0;
  border-radius: 6px; padding: 0.2rem 0.55rem; font-size: 0.74rem; font-weight: 600; cursor: pointer;
}
.dir-info, .dir-vacio { font-size: 0.78rem; color: #888; font-style: italic; margin: 0.5rem 0; }

.dir-tabla { width: 100%; border-collapse: collapse; font-size: 0.76rem; margin-top: 0.6rem; }
.dir-tabla th { text-align: left; color: #6b7280; font-weight: 600; padding: 0.25rem 0.4rem; }
.dir-tabla td { padding: 0.3rem 0.4rem; border-top: 1px solid #f0f3f6; }
.dir-tabla tr.saliente { opacity: 0.65; }
.dir-badge {
  background: #f0f2f5; color: #6b7280; border-radius: 999px;
  padding: 1px 6px; font-size: 0.68rem; margin-left: 0.3rem;
}
.dir-acciones { white-space: nowrap; }
.btn-dir {
  border: 1px solid #d6dee6; background: white; color: #4a5b6b;
  border-radius: 6px; padding: 0.15rem 0.45rem; font-size: 0.72rem; cursor: pointer; margin-left: 0.2rem;
}
.btn-dir:hover { background: #f2f6fa; }
.btn-dir-ok { background: #14507d; color: white; border-color: #14507d; }
.btn-dir-peligro { color: #b3261e; }

.dir-form { border: 1px solid #c6ddf7; background: #f7fbff; border-radius: 8px; padding: 0.8rem; margin-top: 0.7rem; }
.dir-form h5 { margin: 0 0 0.6rem; font-size: 0.82rem; color: #14507d; }
.dir-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(175px, 1fr)); gap: 0.6rem; }
.dir-grid label { display: flex; flex-direction: column; gap: 0.2rem; font-size: 0.74rem; color: #4a5b6b; }
.dir-input { border: 1px solid #d6dee6; border-radius: 6px; padding: 0.25rem 0.4rem; font-size: 0.76rem; }
.dir-ayuda { font-size: 0.68rem; color: #9aa5b1; }
.dir-form-acciones { display: flex; gap: 0.4rem; justify-content: flex-end; margin-top: 0.7rem; }
.dir-error { margin: 0.5rem 0 0; font-size: 0.75rem; color: #b3261e; }
</style>
